import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { ResearchGraphic } from '@/components/research-graphic';
import { VeritantWordmark } from '@/components/veritant-wordmark';
import { Lockup, ZoneProvider, useDocumentTitle, useZoneRoot } from '@/design';
import { useAuth } from '../auth-context';
import { HELP_ROUTE, HOME_ROUTE } from '../constants';

/**
 * The front door: every screen a visitor meets before a portal's shell draws — sign-in and its
 * callback, the request for an invitation, the address confirmation, registration, and the two
 * pages that turn somebody away at a portal that is not theirs (the two-portals design, §3.4).
 *
 * **It is the Advisor Portal's front door, in its character**: the same white bar and lockup, the
 * Off White ground, and — beside a door's form, at desktop — the portal's gradient panel with the
 * guidelines' statement copy. A Team Portal account signs in through it too and lands in the Team
 * Portal, because one sign-in serves both and the account's kind decides where it lands.
 *
 * **Its bar has no navigation and no button.** A link home through the lockup, and one to Help,
 * which is reachable signed out because registration's refusals point there. Help is the Advisor
 * Portal's, so it is not offered to a signed-in Team Portal account — for it, it is a portal
 * address, and would only draw the boundary page. No button because
 * each registration page says what its wireframe says — page 01's one button is Continue — and
 * the suite holds it to that; no menu because nobody here is signed in to a portal yet.
 *
 * Two shapes. `door` is the form beside the panel; `page` is one reading column, for Help and the
 * two boundary pages, whose content is not a form. `zoneName` is the portal the frame names: the
 * Advisor Portal by default, and none — the wordmark alone — on the boundary pages, which belong
 * to neither portal.
 */
export function AuthShell({
  title,
  shape = 'door',
  zoneName = 'Advisor Portal',
  children
}: {
  /** The tab's title. */
  title: string;
  shape?: 'door' | 'page';
  zoneName?: Nullable<string>;
  children: React.ReactNode;
}) {
  useZoneRoot('front-door');
  const { user } = useAuth();
  const offersHelp = !user || user.kind === 'advisor';

  return (
    <ZoneProvider zone="front-door">
      <FrontDoorTitle title={title} />
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <header className="flex h-16 shrink-0 items-center gap-6 border-b border-border bg-white px-6 lg:px-10">
          <Link
            to={HOME_ROUTE}
            className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {zoneName ? (
              <Lockup zoneName={zoneName} />
            ) : (
              <VeritantWordmark tone="sapphire" className="-mx-[0.33em] text-[1.625rem]" />
            )}
          </Link>
          {offersHelp && (
            <Link
              to={HELP_ROUTE}
              className="ml-auto text-[0.9375rem] text-muted-foreground underline-offset-4 hover:text-[var(--color-royal-blue)] hover:underline"
            >
              Help
            </Link>
          )}
        </header>

        <main
          className={cn(
            'flex flex-1',
            shape === 'door' && 'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'
          )}
        >
          <section
            className={cn(
              'flex w-full justify-center px-6 py-12 sm:py-16',
              shape === 'door' ? 'lg:items-center lg:py-20' : 'lg:py-16'
            )}
          >
            {/* Every screen's title is set here, once, rather than at each of the dozen `h1`s the
                screens behind this door draw: a light 32 px Royal Blue, the portal's voice. */}
            <div
              className={cn(
                'w-full [&_h1]:text-[2rem] [&_h1]:leading-tight [&_h1]:font-light [&_h1]:tracking-[-0.01em] [&_h1]:text-[var(--color-royal-blue)]',
                shape === 'door' ? 'max-w-[26rem]' : 'max-w-[42rem]'
              )}
            >
              {children}
            </div>
          </section>

          {shape === 'door' && (
            <aside className="veritant-gradient-royal-sapphire relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-end lg:p-14">
              {/* Cropped off the corner and clear of the statement copy — the graphics add
                  depth, they do not sit behind words (guidelines p.11). */}
              <ResearchGraphic
                name="rising-benchmarks"
                className="pointer-events-none absolute -top-24 -right-32 h-[34rem] w-[34rem] text-[var(--color-bright-blue)] opacity-35"
              />
              {/* The Short Value Proposition, quoted from the messaging guidelines. */}
              <div className="relative max-w-md">
                <p className="text-[2rem] leading-tight font-light text-white">
                  We measure twice, publish once.
                </p>
                <p className="mt-4 text-[1.0625rem] text-[var(--color-slight-blue)]">
                  Because defining excellence demands precision.
                </p>
              </div>
            </aside>
          )}
        </main>
      </div>
    </ZoneProvider>
  );
}

/** Inside the provider, so the title takes the front door's prefix. */
function FrontDoorTitle({ title }: { title: string }) {
  useDocumentTitle(title);
  return null;
}

/**
 * The heading and lede every front-door screen opens with, in the portal's voice: a light 32 px
 * title in Royal Blue, and the lede under it. The screen's one `h1`.
 */
export function DoorHeading({ title, lede }: { title: string; lede?: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h1 className="text-[2rem] leading-tight font-light tracking-[-0.01em] text-[var(--color-royal-blue)]">
        {title}
      </h1>
      {lede && (
        <div className="mt-3 text-[0.9375rem] leading-relaxed text-muted-foreground">{lede}</div>
      )}
    </div>
  );
}
